import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Calendar } from 'lucide-react';
import ShowRegionButton from '../components/map/ShowRegionButton';
import ExchangesPanel from '../components/map/ExchangesPanel';
import { startBackgroundPrefetch } from '../services/mapDataCache';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, ZoomControl } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { toast } from 'sonner';
import 'leaflet/dist/leaflet.css';
import '@/styles/leaflet-dark.css';
import L from 'leaflet';
import { useTheme } from '@/contexts/ThemeContext';

import {
  API,
  orangeIcon, btcmapIcon, atmIcon, eventIcon,
  QUICK_CATEGORIES,
  isATM, matchesCategoryOrTag, getMarkerCoords, isBusinessVisible,
  MapBoundsTracker, FlyToLocation, useUserLocation,
  SearchBar, CategoryPills, Sidebar, BusinessPopup, EventPopup,
  EditSuggestionDialog, MapLegend
} from '@/components/map';
import { useTileConfig, tileLayerProps } from '@/components/map/tiles';

import REGIONS from '@/data/regions.json';
import COUNTRIES from '@/data/countries.json';
import CATEGORIES_DATA from '@/data/categories.json';
import { isGlobalScope } from '../utils/businessScope';

const VALID_SLUGS = Object.keys(REGIONS);

// Compute the union bounding box for all countries in a region
const getRegionBounds = (slug) => {
  const regionDef = REGIONS[slug];
  if (!regionDef) return null;

  let latMin = 90, latMax = -90, lonMin = 180, lonMax = -180;
  let found = false;

  for (const iso2 of regionDef.countries) {
    const c = COUNTRIES[iso2];
    if (!c) continue;
    const { lat_min, lat_max, lon_min, lon_max } = c.bbox;
    latMin = Math.min(latMin, lat_min);
    latMax = Math.max(latMax, lat_max);
    lonMin = Math.min(lonMin, lon_min);
    lonMax = Math.max(lonMax, lon_max);
    found = true;
  }

  if (!found) return null;

  // Clamp to valid Leaflet ranges
  latMin = Math.max(latMin, -85);
  latMax = Math.min(latMax, 85);
  lonMin = Math.max(lonMin, -180);
  lonMax = Math.min(lonMax, 180);

  return L.latLngBounds(L.latLng(latMin, lonMin), L.latLng(latMax, lonMax));
};

// Build a set of ISO2 codes for a region slug
const getRegionCodes = (slug) => {
  const regionDef = REGIONS[slug];
  if (!regionDef) return new Set();
  return new Set(regionDef.countries.map(c => c.toUpperCase()));
};

const isInRegion = (b, regionCodes, regionBounds, regionSlug) => {
  // Map view = "who's BASED in this region?" — not "who serves it?".
  // Global-scope records are excluded (use /directory for service-based
  // filters) via the shared predicate, keeping this symmetric with the
  // backend and BusinessMapCountry. Region-scope OL records (e.g.
  // service_scope='region', service_region='europe') DO count as based-in
  // because they have no finer location and declared this region as home.
  if (isGlobalScope(b)) return false;
  if (b.service_scope === 'region' && b.service_region && regionSlug) {
    if (b.service_region.toLowerCase() === regionSlug.toLowerCase()) return true;
  }
  // Country-based businesses: in-region if their country_code is.
  const cc = (b.country_code || '').toUpperCase();
  if (cc) return regionCodes.has(cc);
  // Legacy records without country_code: coord fallback.
  const lat = b.latitude || b.lat;
  const lng = b.longitude || b.lon;
  if (lat && lng && regionBounds) {
    return regionBounds.contains(L.latLng(lat, lng));
  }
  return false;
};

const isEventInRegion = (e, regionCodes, regionBounds) => {
  if (e.country && regionCodes.has(e.country.toUpperCase())) return true;
  if (e.latitude && e.longitude && regionBounds) {
    return regionBounds.contains(L.latLng(e.latitude, e.longitude));
  }
  return false;
};

const makeClusterIcon = (bg) => (cluster) => L.divIcon({
  html: `<div style="background:${bg};color:white;border-radius:50%;width:36px;height:36px;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:13px;border:2px solid white;box-shadow:0 2px 5px rgba(0,0,0,0.3);">${cluster.getChildCount()}</div>`,
  className: '',
  iconSize: L.point(36, 36, true)
});

const BusinessMapRegion = () => {
  const { slug } = useParams();

  // Validate slug without early-returning — React's Rules of Hooks require
  // every hook below to run in the same order on every render. Navigation
  // happens at the bottom of the render via the `navigateAway` guard.
  const navigateAway = !slug || !VALID_SLUGS.includes(slug);
  const safeSlug = navigateAway ? VALID_SLUGS[0] : slug;

  const regionDef = REGIONS[safeSlug];
  const regionCodes = useMemo(() => getRegionCodes(safeSlug), [safeSlug]);
  const regionBounds = useMemo(() => getRegionBounds(safeSlug), [safeSlug]);

  // Build state-bounds for isBusinessVisible
  const stateBoundsForRegion = useMemo(() => {
    if (!regionBounds) return {};
    return { REGION: regionBounds };
  }, [regionBounds]);

  // Core data state
  const [allBusinesses, setAllBusinesses] = useState([]);
  const [events, setEvents] = useState([]);
  const [mapEvents, setMapEvents] = useState([]);
  const [mapTags, setMapTags] = useState([{ name: 'All', categories: [] }]);
  const categories = useMemo(
    () => CATEGORIES_DATA.filter(c => c.slug).map(c => c.name).sort((a, b) => a.localeCompare(b)),
    []
  );
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [exchanges, setExchanges] = useState(null);

  // Map state
  const [mapBounds, setMapBounds] = useState(null);
  const [flyToCoords, setFlyToCoords] = useState(null);
  const [flyTrigger, setFlyTrigger] = useState(0);
  const [pinnedPopup, setPinnedPopup] = useState(null);
  const [pinnedEventId, setPinnedEventId] = useState(null);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showOrangeLabel, setShowOrangeLabel] = useState(true);
  const [showBtcMap, setShowBtcMap] = useState(true);
  const [showATMs, setShowATMs] = useState(false);
  const [showEvents, setShowEvents] = useState(true);

  // UI state
  const [showSidebar, setShowSidebar] = useState(true);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [searchFocused, setSearchFocused] = useState(false);

  // Edit suggestion state
  const [editSuggestionDialog, setEditSuggestionDialog] = useState(false);
  const [editSuggestionBusiness, setEditSuggestionBusiness] = useState(null);
  const [editSuggestionForm, setEditSuggestionForm] = useState({
    website: '', contact: '', address: '', categories: [],
    submitterName: '', submitterEmail: ''
  });
  const [submittingSuggestion, setSubmittingSuggestion] = useState(false);

  // Dark mode tile switching via ThemeContext
  const { darkMode } = useTheme();
  const tileConfig = useTileConfig();
  const tileLayer = tileLayerProps(darkMode, tileConfig);

  const { locateUser } = useUserLocation(setFlyToCoords, setFlyTrigger);

  useEffect(() => {
    setLoading(true);
    setFetchError(false);
    const codes = getRegionCodes(safeSlug);
    const bounds = getRegionBounds(safeSlug);

    const fetchData = async () => {
      // Per-request .catch — see matching comment in BusinessMapCountry.
      // Keeps the exchanges bar from getting stuck at null on mobile Safari
      // when /businesses is slow or transiently 524s. The businesses request
      // flags __error so a failed fetch shows a retry banner instead of
      // masquerading as "no listings".
      const [businessRes, tagsRes, eventsRes, mapEventsRes, exchangesRes] = await Promise.all([
        axios.get(`${API}/businesses?region_slug=${safeSlug}`).catch(() => ({ data: [], __error: true })),
        axios.get(`${API}/admin/map-tags`).catch(() => ({ data: { tags: [] } })),
        axios.get(`${API}/events`).catch(() => ({ data: [] })),
        axios.get(`${API}/events?for_map=true`).catch(() => ({ data: [] })),
        axios.get(`${API}/exchanges?region=${safeSlug}`).catch(() => ({ data: [] })),
      ]);

      setFetchError(!!businessRes.__error);
      setAllBusinesses((businessRes.data || []).filter(b => isInRegion(b, codes, bounds, safeSlug)));

      if (tagsRes.data?.tags?.length > 0) {
        setMapTags(tagsRes.data.tags);
      } else {
        setMapTags(QUICK_CATEGORIES.map(c => ({ name: c, categories: c === 'All' ? [] : [c] })));
      }
      setEvents((eventsRes.data || []).filter(e => isEventInRegion(e, codes, bounds)));
      setMapEvents((mapEventsRes.data || []).filter(e => isEventInRegion(e, codes, bounds)));
      setExchanges(Array.isArray(exchangesRes.data) ? exchangesRes.data : []);
      setLoading(false);

      // Kick off background prefetch for other regions once initial data is ready.
      startBackgroundPrefetch(safeSlug);
    };
    fetchData();
  }, [safeSlug, reloadKey]);

  const handleBoundsChange = useCallback((bounds) => setMapBounds(bounds), []);
  const resetFilters = useCallback(() => { setSearchQuery(''); setSelectedCategory('All'); }, []);
  const hasActiveFilters = useMemo(() => searchQuery || selectedCategory !== 'All', [searchQuery, selectedCategory]);

  const handleCardClick = useCallback((b) => {
    setSelectedBusiness(b);
    const coords = getMarkerCoords(b);
    setFlyToCoords(coords);
    setFlyTrigger(prev => prev + 1);
  }, []);

  const handleFlyToEvent = useCallback((event) => {
    if (event.latitude && event.longitude) {
      setFlyToCoords([event.latitude, event.longitude]);
      setFlyTrigger(prev => prev + 1);
    }
  }, []);

  const openEditSuggestion = useCallback((business) => {
    setEditSuggestionBusiness(business);
    setEditSuggestionForm({
      website: business.website || '',
      contact: business.contact || '',
      address: `${business.based_state || ''} ${business.region || ''}`.trim(),
      categories: business.category || [],
      submitterName: '', submitterEmail: ''
    });
    setEditSuggestionDialog(true);
  }, []);

  const submitEditSuggestion = async () => {
    if (!editSuggestionBusiness) return;
    setSubmittingSuggestion(true);
    try {
      const changes = {};
      if (editSuggestionForm.website !== (editSuggestionBusiness.website || '')) {
        changes.website = { old: editSuggestionBusiness.website || '', new: editSuggestionForm.website };
      }
      if (editSuggestionForm.contact !== (editSuggestionBusiness.contact || '')) {
        changes.contact = { old: editSuggestionBusiness.contact || '', new: editSuggestionForm.contact };
      }
      if (editSuggestionForm.address !== `${editSuggestionBusiness.based_state || ''} ${editSuggestionBusiness.region || ''}`.trim()) {
        changes.region = { old: editSuggestionBusiness.region || '', new: editSuggestionForm.address };
      }
      const origCats = (editSuggestionBusiness.category || []).sort().join(',');
      const newCats = (editSuggestionForm.categories || []).sort().join(',');
      if (origCats !== newCats) {
        changes.category = { old: editSuggestionBusiness.category || [], new: editSuggestionForm.categories };
      }
      if (Object.keys(changes).length === 0) {
        toast.error('No changes detected');
        setSubmittingSuggestion(false);
        return;
      }
      await axios.post(`${API}/suggestions`, {
        business_id: editSuggestionBusiness.id,
        changes,
        name: editSuggestionForm.submitterName,
        email: editSuggestionForm.submitterEmail
      });
      toast.success('Thank you! Your suggestion has been submitted for review.');
      setEditSuggestionDialog(false);
    } catch (e) {
      toast.error('Failed to submit suggestion');
    }
    setSubmittingSuggestion(false);
  };

  const sourceFilteredBusinesses = useMemo(() => {
    let f = [...allBusinesses];
    if (selectedCategory !== 'All') {
      f = f.filter(b => matchesCategoryOrTag(b, selectedCategory, mapTags));
    }
    const atms = f.filter(isATM);
    const nonAtms = f.filter(b => !isATM(b));
    let r = [];
    if (showOrangeLabel) r = r.concat(nonAtms.filter(b => !b.btc_map));
    if (showBtcMap) r = r.concat(nonAtms.filter(b => b.btc_map));
    if (showATMs) r = r.concat(atms);
    return r;
  }, [allBusinesses, showOrangeLabel, showBtcMap, showATMs, selectedCategory, mapTags]);

  const visibleBusinesses = useMemo(() => {
    let businesses = sourceFilteredBusinesses.filter(b => isBusinessVisible(b, mapBounds, stateBoundsForRegion));
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      businesses = businesses.filter(b =>
        (b.business_name || '').toLowerCase().includes(q) ||
        (b.category || []).some(c => c.toLowerCase().includes(q)) ||
        (b.region || '').toLowerCase().includes(q)
      );
    }
    return businesses;
  }, [sourceFilteredBusinesses, mapBounds, searchQuery, stateBoundsForRegion]);

  const createOrangeCluster = useCallback(makeClusterIcon('#f97316'), []);
  const createBtcmapCluster = useCallback(makeClusterIcon('#0891B2'), []);
  const createAtmCluster = useCallback(makeClusterIcon('#8b5cf6'), []);

  const orangeLabelBusinesses = useMemo(() => sourceFilteredBusinesses.filter(b => !b.btc_map && !isATM(b)), [sourceFilteredBusinesses]);
  const btcmapBusinesses = useMemo(() => sourceFilteredBusinesses.filter(b => b.btc_map && !isATM(b)), [sourceFilteredBusinesses]);
  const atmBusinesses = useMemo(() => sourceFilteredBusinesses.filter(isATM), [sourceFilteredBusinesses]);
  const visibleMapEvents = useMemo(() => mapEvents.filter(e => e.latitude && e.longitude), [mapEvents]);

  const getEventPosition = useCallback((event) => {
    const eventLat = event.latitude;
    const eventLng = event.longitude;
    const hasOverlap = allBusinesses.some(b => {
      const bLat = b.latitude || b.lat;
      const bLng = b.longitude || b.lon;
      if (!bLat || !bLng) return false;
      return Math.abs(bLat - eventLat) < 0.0005 && Math.abs(bLng - eventLng) < 0.0005;
    });
    if (hasOverlap) return [eventLat + 0.0003, eventLng + 0.0004];
    return [eventLat, eventLng];
  }, [allBusinesses]);

  const toggleSidebar = useCallback(() => setShowSidebar(prev => !prev), []);

  const createMarkerHandlers = useCallback((b) => ({
    click: (e) => { setPinnedPopup(b.id); e.target.openPopup(); },
    mouseover: (e) => { if (pinnedPopup !== b.id) e.target.openPopup(); },
    mouseout: (e) => { if (pinnedPopup !== b.id) e.target.closePopup(); }
  }), [pinnedPopup]);

  const regionName = regionDef?.name || safeSlug;
  const countryCount = regionDef?.countries?.length || 0;

  // Guard: route-param was invalid — redirect now that all hooks have run.
  if (navigateAway) {
    return <Navigate to="/map/au" replace />;
  }

  if (loading) {
    return (
      <div className="h-[calc(100dvh-64px)] flex items-center justify-center bg-muted">
        <p className="text-muted-foreground">Loading {regionName} map...</p>
      </div>
    );
  }

  const mapProps = regionBounds
    ? { bounds: regionBounds, boundsOptions: { padding: [20, 20] } }
    : { center: [20, 0], zoom: 2 };

  return (
    <div className="flex flex-col" style={{ height: 'calc(100dvh - 64px)' }}>
      <div className="relative flex-1 min-h-0">

        {fetchError && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[500] bg-destructive text-destructive-foreground rounded-lg shadow-lg px-4 py-2 text-sm flex items-center gap-3">
            <span>Couldn&apos;t load listings — this is a connection issue, not an empty area.</span>
            <button
              onClick={() => setReloadKey(k => k + 1)}
              className="underline font-medium whitespace-nowrap hover:opacity-80"
            >
              Retry
            </button>
          </div>
        )}

        <MapContainer
          {...mapProps}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
          zoomControl={false}
        >
          <ZoomControl position="bottomright" />
          <TileLayer {...tileLayer} />
          <MapBoundsTracker onBoundsChange={handleBoundsChange} />
          <FlyToLocation coords={flyToCoords} trigger={flyTrigger} />

          {showOrangeLabel && (
            <MarkerClusterGroup chunkedLoading iconCreateFunction={createOrangeCluster}>
              {orangeLabelBusinesses.map(b => {
                const c = getMarkerCoords(b);
                return c && (
                  <Marker key={`ol-${b.id}`} position={c} icon={orangeIcon} eventHandlers={createMarkerHandlers(b)}>
                    <Popup closeButton={true} onClose={() => { if (pinnedPopup === b.id) setPinnedPopup(null); }}>
                      <BusinessPopup business={b} onSuggestEdit={openEditSuggestion} />
                    </Popup>
                  </Marker>
                );
              })}
            </MarkerClusterGroup>
          )}

          {showBtcMap && (
            <MarkerClusterGroup chunkedLoading iconCreateFunction={createBtcmapCluster}>
              {btcmapBusinesses.map(b => {
                const c = getMarkerCoords(b);
                return c && (
                  <Marker key={`btc-${b.id}`} position={c} icon={btcmapIcon} eventHandlers={createMarkerHandlers(b)}>
                    <Popup closeButton={true} onClose={() => { if (pinnedPopup === b.id) setPinnedPopup(null); }}>
                      <BusinessPopup business={b} onSuggestEdit={null} />
                    </Popup>
                  </Marker>
                );
              })}
            </MarkerClusterGroup>
          )}

          {showATMs && (
            <MarkerClusterGroup chunkedLoading iconCreateFunction={createAtmCluster}>
              {atmBusinesses.map(b => {
                const c = getMarkerCoords(b);
                return c && (
                  <Marker key={`atm-${b.id}`} position={c} icon={atmIcon} eventHandlers={createMarkerHandlers(b)}>
                    <Popup closeButton={true} onClose={() => { if (pinnedPopup === b.id) setPinnedPopup(null); }}>
                      <BusinessPopup business={b} onSuggestEdit={null} />
                    </Popup>
                  </Marker>
                );
              })}
            </MarkerClusterGroup>
          )}

          {showEvents && visibleMapEvents.map(event => (
            <Marker
              key={`event-${event.id}`}
              position={getEventPosition(event)}
              icon={eventIcon}
              zIndexOffset={1000}
              eventHandlers={{
                click: (e) => {
                  if (pinnedEventId === event.id) {
                    setPinnedEventId(null);
                    e.target.closePopup();
                  } else {
                    setPinnedEventId(event.id);
                    e.target.openPopup();
                  }
                },
                mouseover: (e) => { e.target.openPopup(); },
                mouseout: (e) => { if (pinnedEventId !== event.id) e.target.closePopup(); }
              }}
            >
              <Popup
                className="event-popup"
                maxWidth={300}
                autoClose={false}
                closeOnClick={false}
                onClose={() => { if (pinnedEventId === event.id) setPinnedEventId(null); }}
              >
                <EventPopup
                  event={event}
                  isPinned={pinnedEventId === event.id}
                  onClose={() => { setPinnedEventId(null); }}
                />
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        <SearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchFocused={searchFocused}
          setSearchFocused={setSearchFocused}
          visibleBusinesses={visibleBusinesses}
          onSelectBusiness={handleCardClick}
          onToggleSidebar={toggleSidebar}
          onLocateUser={locateUser}
          hasActiveFilters={hasActiveFilters}
          onResetFilters={resetFilters}
        />

        {/* Show-Region affordance — only renders when user has panned outside the current region */}
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-[490] pointer-events-none">
          <ShowRegionButton mapBounds={mapBounds} currentRegionSlug={safeSlug} />
        </div>

        <CategoryPills
          mapTags={mapTags}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
        />

        <MapLegend />

        <button
          onClick={toggleSidebar}
          className="hidden md:block absolute top-4 right-4 z-[400] bg-background border border-border rounded-lg shadow-lg px-3 py-2 text-sm font-medium text-foreground hover:bg-accent"
        >
          {showSidebar ? 'Hide List' : 'Show List'}
        </button>

        <Sidebar
          showSidebar={showSidebar}
          setShowSidebar={setShowSidebar}
          visibleBusinesses={visibleBusinesses}
          events={events}
          mapBounds={mapBounds}
          showOrangeLabel={showOrangeLabel}
          setShowOrangeLabel={setShowOrangeLabel}
          showBtcMap={showBtcMap}
          setShowBtcMap={setShowBtcMap}
          showEvents={showEvents}
          setShowEvents={setShowEvents}
          showATMs={showATMs}
          setShowATMs={setShowATMs}
          selectedBusiness={selectedBusiness}
          onSelectBusiness={handleCardClick}
          onFlyToEvent={handleFlyToEvent}
          hasActiveFilters={hasActiveFilters}
          resetFilters={resetFilters}
        />

        <EditSuggestionDialog
          open={editSuggestionDialog}
          onOpenChange={setEditSuggestionDialog}
          business={editSuggestionBusiness}
          form={editSuggestionForm}
          setForm={setEditSuggestionForm}
          categories={categories}
          onSubmit={submitEditSuggestion}
          isSubmitting={submittingSuggestion}
        />
      </div>

      {/* Exchanges panel below map */}
      <ExchangesPanel exchanges={exchanges} />
    </div>
  );
};

export default BusinessMapRegion;
